<script setup>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";

// Components
import UpdateUserModal from "@/views/Users/UpdateUser.vue";
import UpdatePasswordModal from "@/views/Users/UpdatePassword.vue";
import generateUserProfile from "@/views/auth/domain/generate-user-profile"; // Import the function
import paymentCard from "@/views/profile/components/Payments.vue";
import paymentByCourse from "@/views/profile/components/PaymentsByCourse.vue";

import BarChart from "@/views/profile/components/BarChart.vue";
import OutlinedCounterCard from "@/views/profile/components/OutlinedCounterCard.vue";
import { TilopayClient } from "@/services/TiloPayClient";
import { CoursesClient } from "@/services/CoursesClient";

const coursesClient = new CoursesClient();
const tilopayClient = new TilopayClient();
const token = sessionStorage.getItem("token"); // Assuming your token is stored with the key 'reset_token'

// Estado de visibilidad de los modales
const showUserModal = ref(false);
const showPasswordModal = ref(false);
const store = useStore();
const instructorCourses = ref([]); // Store courses data here

if (window.innerWidth <= 768) { // Adjust breakpoint as needed
  store.commit("navbarMinimize");
}

// Abre el modal correspondiente
const openModal = (type) => {
  if (type === "user") showUserModal.value = true;
  if (type === "password") showPasswordModal.value = true;
};

// Cierra todos los modales
const closeModal = () => {
  showUserModal.value = false;
  showPasswordModal.value = false;
};

// Estado de la información del perfil
const userProfile = ref({
  nombre: "",
  apellido: "",
  email: "",
  mobile: "",
  rol: "",
});

const dashboardData = ref({
  total_colones: 0,
  total_dolares: 0,
  total_students: 0,
  exchange_rate: {
    buy_rate: 0,
    sell_rate: 0,
    date: ''
  }
});

const loading = ref(true);
const error = ref(null);


const fetchDashboardSummary = async () => {
  try {
    loading.value = true;
    const response = await tilopayClient.get('/dashboard/summary');

    if (response) {
      dashboardData.value = {
        total_colones: response.total_colones || 0,
        total_dolares: response.total_dolares || 0,
        total_students: response.total_students || 0,
        exchange_rate: {
          buy_rate: response.exchange_rate?.buy_rate || 0,
          sell_rate: response.exchange_rate?.sell_rate || 0,
          date: response.exchange_rate?.date || ''
        },
        students_per_course: response.students_per_course || {}

      };
    }
    
  } catch (err) {
    console.error("Error loading dashboard summary:", err);
  } finally {
    loading.value = false;
  }
};

const fetchInstructorCourses = async () => {
  try {
    loading.value = true;
    const response = await coursesClient.getInstructorCoursesById();   
    
    if (response?.length) {
      instructorCourses.value = response.map(course => ({
        id: course.id,
        title: course.titulo || course.name || 'Curso sin nombre',
        description: course.description || ''
      }));
    }
  } catch (err) {
    console.error("Error loading courses:", err);
  } finally {
    loading.value = false;
  }
};

const prepareChartData = () => {
  if (!dashboardData.value.students_per_course || !instructorCourses.value.length) {
    return {
      labels: [],
      datasets: [{ data: [] }]
    };
  }

  // Map course IDs to their titles
  const courseMap = {};
  instructorCourses.value.forEach(course => {
    courseMap[course.id] = course.title;
  });

  const coursesData = Object.entries(dashboardData.value.students_per_course)
    .map(([id, count]) => ({
      id,
      title: courseMap[id] || `Curso ${id}`,
      count
    }))
    .sort((a, b) => b.count - a.count); // sort by student count

  return {
    labels: coursesData.map(course => course.title),
    datasets: [{
      label: 'Estudiantes',
      backgroundColor: ['#2dce89', '#FF4767', '#344767', '#f4a261', '#e76f51'],
      data: coursesData.map(course => course.count)
    }]
  };
};

onMounted(async () => {
  try {
    await fetchUserProfile();
    
    // Only fetch instructor courses if user is an instructor (role_id 3)
    if (userProfile.value?.rol === 3) {
      console.log("Fetching instructor courses...");
      
      await fetchDashboardSummary();
      await fetchInstructorCourses();
    }
    
  } catch (error) {
    console.error("Error in Overview component initialization:", error);
  }
});

const fetchUserProfile = async () => {
  try {

    const data = await generateUserProfile();

    // Asignar la información del perfil al estado
    userProfile.value = {
      id: data.id_usuario, // Ensure this is available
      email: data.email,
      mobile: String(data.telefono || "No disponible"), // Force string conversion
      nombre: data.nombre,
      rol: data.role_id,
    };



    loading.value = false;
  } catch (err) {
    error.value = "Hubo un problema al cargar el perfil del usuario.";
    loading.value = false;
  }

};

</script>

<template>
  <section>

    <div class="page-header min-vh-20 align-items-start pt-0 pb-10">
      <span class="mask bg-gradient-dark opacity-6"></span>
    </div>
    <!-- Mostrar error o cargando -->
    <div v-if="loading" class="text-center mt-4">
      <p>Cargando...</p>
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>
    <div v-else-if="error" class="text-danger text-center">{{ error }}</div>
    <div v-else>

      <!-- Tarjeta de perfil -->
      <div class="card shadow-lg mx-4 mt-3 mb-1 card-profile-bottom mt-lg-n5 mt-n6">

        <div class="card shadow-lg">

          <div class="card-body p-3">
            <div class="row gx-4">
              <div class="col-auto">
                <div class="avatar-xl position-relative d-flex align-items-center justify-content-center">
                  <i class="fa fa-user fa-4x text-dark"></i>
                </div>
              </div>
              <div class="col-auto my-auto">
                <div class="h-100">
                  <h5 class="mb-1">
                    {{ userProfile.nombre }} {{ userProfile.apellido }}
                  </h5>

                  <span>
                    {{ userProfile.rol === 1 ? "Propietario"
                      : userProfile.rol === 2 ? "Administrador"
                        : userProfile.rol === 3 ? "Instructor"
                          : userProfile.rol === 4 ? "Estudiante"
                            : "Desconocido" }} </span>

                </div>

              </div>
              <!-- Botón para acciones de edición -->
              <div class="col-auto ms-auto d-flex">
                <div class="dropdown-xs">
                  <button class="btn btn-lg pt-1 pb-1 ps-3 pe-3 m-0 dropdown-toggle" type="button"
                    data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fas fa-cog"></i>
                  </button>
                  <ul class="dropdown-menu w-20">
                    <li>
                      <a class="dropdown-item" @click="openModal('user')"><i class="fas fa-user-edit pe-1"></i> Editar
                        Informacion</a>
                    </li>
                    <li>
                      <a class="dropdown-item" @click="openModal('password')"><i class="fas fa-key pe-2"></i> Editar
                        Contraseña</a>
                    </li>
                  </ul>
                </div>

              </div>

            </div>
          </div>
        </div>
      </div>

      <div class="container-fluid py-4">
        <div class="row">

          <!-- Tarjeta de Información de Perfil -->
          <div class="col-12 col-md-4 col-xl-3 mt-2 mt-md-0">
            <div class="card h-100 shadow-lg">
              <div class="p-3 pb-0 card-header">
                <h6 class="mb-0">Información de Contacto</h6>
              </div>
              <div class="p-3 card-body">
                <ul class="list-group">

                  <li class="text-sm border-0 list-group-item ps-0">
                    <strong class="text-xs text-uppercase text-body font-weight-bolder d-block"><i
                        class="fas fa-phone-square-alt pe-2"></i> Teléfono:</strong>
                    <span>{{ userProfile.mobile }}</span>
                  </li>
                  <li class="text-sm border-0 list-group-item ps-0">
                    <strong class="text-xs text-uppercase text-body font-weight-bolder d-block"><i
                        class="fas fa-envelope pe-2"></i> Correo
                      Electrónico:</strong>
                    <span>{{ userProfile.email }}</span>
                  </li>

                </ul>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-9 mt-2 mt-md-0">
            <div class="card-header h-100 shadow-lg">
              <div v-if="userProfile.rol === 3" class="row p-0 p-md-2 py-2">
                <outlined-counter-card prefix="$" :count="dashboardData.total_dolares"
                  title="Ingresos Totales ($)" :decimals="2" />
                <outlined-counter-card class="mt-4 mt-lg-0" prefix="₡" :count="dashboardData.total_colones"
                  title="Ingresos Totales (₡)"  />          
                <outlined-counter-card class="mt-4 mt-lg-0" :count="dashboardData.total_students"
                  title="Total de Estudiantes" :decimals="0"/>
                
              </div>
              <div class="row mt-3" v-if="dashboardData.exchange_rate.date">
                <div class="col-12 text-center text-muted text-xs ">
                  Tipo de cambio: Compra ₡{{ dashboardData.exchange_rate.buy_rate.toFixed(2) }} | 
                  Venta ₡{{ dashboardData.exchange_rate.sell_rate.toFixed(2) }} |
                  Actualizado: {{ dashboardData.exchange_rate.date }}
                </div>
              </div>
              <hr v-if="userProfile.rol === 3" class="horizontal dark" />

              <div v-if="userProfile.rol === 3">
                <payment-by-course />
              </div>
              <div v-else>
                <payment-card />
              </div>
            </div>


          </div>

        </div>

        <div v-if="userProfile.rol === 3" class="row mt-4">
          <div class="col-12 mt-2 mt-md-0">
            
            <bar-chart
              title="Estudiantes por Curso"
              :chart="prepareChartData()"
            />
          </div>

        </div>
      </div>
    </div>
    <!-- Modales -->
    <update-user-modal v-if="showUserModal" :id="userProfile.id" :name="userProfile.nombre" :email="userProfile.email"
      :telefono="userProfile.mobile" :role_id="userProfile.rol" @close="closeModal" />
    <update-password-modal v-if="showPasswordModal" :token="token" @close="closeModal" />
  </section>
</template>

<style scoped>
.profile-overview {
  background-color: #f8f9fa;
}

.card-profile-bottom {
  margin-top: -100px;
  border-radius: 15px;
}

.container-fluid {
  padding-top: 20px;
}

.card-body .col-md-4 {
  padding-left: 10px;
  padding-right: 10px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
  color: #f0f4f8;
}

.card {
  min-height: 100px;
}


.page-header {
  background-image: url('@/assets/img/login-bg.png');
  background-size: unset;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
